// src/datasources/DataSourcesPage.tsx
import * as React from "react";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  IconButton,
  Stack,
  Typography,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import DataSourceIcon from "./DataSourceIcon";
import AddDataSourceDialog from "./AddDataSourceDialog";
import {
  DataSource,
  DeleteDataSourceInput,
  GetDataSourcesInput,
  User,
} from "../../models/API";
import { useEffect, useState } from "react";
import { getDataSources } from "../../graphql/queries";
import { generateClient } from "aws-amplify/api";
import ErrorModal from "../ErrorModal";
import Button from "@mui/material/Button";
import { covertUtcToLocal } from "../utils/formatters";
import { deleteDataSource } from "../../graphql/mutations";
import ConfirmationDialog from "../utils/ConfirmationDialog";
import { useGlobal } from "../../context/GlobalContext";

interface DataSourcesPageProps {
  currentUserId: number;
  currentUserName: string;
  userConfig: User;
  effectiveRole: string;
}

export default function DataSourcesPage({
  currentUserId,
  currentUserName,
  userConfig,
  effectiveRole,
}: DataSourcesPageProps) {
  const [rows, setRows] = React.useState<DataSource[]>([]);
  const [openAdd, setOpenAdd] = React.useState(false);
  const [editRow, setEditRow] = React.useState<DataSource | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [, setDataSourcesData] = useState<DataSource[]>([]);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [error, setError] = useState<any>(null);
  const client = generateClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(0);
  const { globalTeamId } = useGlobal();

  async function fetchDataSources() {
    try {
      const getDataSourcesInput: GetDataSourcesInput = {
        user_id: currentUserId,
        team_id: globalTeamId,
      };
      const dataSourcesData = await client.graphql({
        query: getDataSources,
        variables: {
          input: getDataSourcesInput,
        },
      });
      // @ts-ignore
      const dataSources: DataSource[] = dataSourcesData.data.getDataSources;
      setDataSourcesData(dataSources);
      setRows(dataSources);

      console.log("fetchDataSources() refreshed");
    } catch (err) {
      console.error(err);
      setError(err);
    }
  }

  useEffect(() => {
    console.log("User: [" + currentUserId + "] data sources is refreshing...");
    fetchDataSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshFlag, globalTeamId]);

  const onRefresh = async () => {
    setLoading(true);
    setRefreshFlag(!refreshFlag);
    setLoading(false);
  };

  const onDelete = async (id: number) => {
    setIsDialogOpen(true);
    setDeleteId(id);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  const handleConfirm = async () => {
    setIsDialogOpen(false);
    try {
      const deleteDataSourceInput: DeleteDataSourceInput = {
        id: deleteId,
      };
      await client.graphql({
        query: deleteDataSource,
        variables: {
          input: deleteDataSourceInput,
        },
      });
      setRefreshFlag(!refreshFlag);
      console.log("fetchDataSources() refreshed");
    } catch (err) {
      console.error(err);
      setError(err);
    }
  };

  const descriptionPreview = (text: string) =>
    text.length > 120 ? text.slice(0, 117) + "…" : text;

  // Helper function to determine if delete should be disabled
  const isDeleteDisabled = (row: DataSource): boolean => {
    const isAdmin = effectiveRole === "admin";
    const isOperator = effectiveRole === "operator";

    // If row is global
    if (row.is_global) {
      // Allow delete only if user is admin/operator AND current team is FINN (team_id === 1)
      return !((isAdmin || isOperator) && globalTeamId === 1);
    }

    // If row is public (but not global)
    if (row.is_public) {
      // Allow delete if user is admin or operator
      return !(isAdmin || isOperator);
    }

    // For non-public, non-global rows, allow delete
    return false;
  };

  // Helper function to get card background color and border color
  const getCardStyle = (row: DataSource) => {
    if (row.is_global) {
      return {
        backgroundColor: "rgba(255, 152, 0, 0.03)",
        borderColor: "#ff9800",
      };
    } else if (row.is_public) {
      return {
        backgroundColor: "rgba(46, 125, 50, 0.03)",
        borderColor: "#2e7d32",
      };
    } else {
      return {
        backgroundColor: "rgba(21, 101, 192, 0.03)",
        borderColor: "#1565c0",
      };
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* Fixed Header */}
      <Box sx={{ flexShrink: 0, p: 2, pb: 0 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h5" fontWeight={700}>
            Data sources
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={onRefresh}
                  disabled={loading}
                  aria-label="refresh"
                >
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAdd(true)}
            >
              Add data source
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          p: 2,
          pt: 1,
        }}
      >
        {rows.length === 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 8,
              px: 4,
              textAlign: "center",
            }}
          >
            <StorageOutlinedIcon
              sx={{ fontSize: 64, color: "grey.300", mb: 2 }}
            />
            <Typography variant="h6" color="text.secondary" fontWeight={500}>
              No data sources yet
            </Typography>
            <Typography
              variant="body2"
              color="text.disabled"
              sx={{ mt: 0.5, maxWidth: 320 }}
            >
              Add data sources to connect external tools and services to your
              knowledge base.
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          {rows.map((row) => {
            const cardStyle = getCardStyle(row);
            return (
              <Grid item xs={12} sm={6} md={4} key={row.id}>
                <Card
                  sx={{
                    backgroundColor: cardStyle.backgroundColor,
                    borderLeft: `6px solid ${cardStyle.borderColor}`,
                    borderRadius: 2,
                    transition: "all 0.25s ease",
                    "&:hover": {
                      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => {
                      if (row.is_global && globalTeamId !== 1) return;
                      setEditRow(row);
                    }}
                    sx={
                      row.is_global && globalTeamId !== 1
                        ? { cursor: "default" }
                        : {}
                    }
                  >
                    <CardHeader
                      avatar={
                        <DataSourceIcon
                          type={row.data_source_type}
                          fontSize="medium"
                        />
                      }
                      title={
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight={700}
                            noWrap
                          >
                            {row.display_name} (ID: {row.id})
                          </Typography>
                          <Chip
                            size="small"
                            label={row.data_source_type}
                            variant="outlined"
                          />
                        </Stack>
                      }
                      subheader={row.tool_name}
                    />
                    <CardContent>
                      <Stack spacing={1}>
                        <Typography variant="body2" color="text.secondary">
                          {descriptionPreview(row.description)}
                        </Typography>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="caption" color="text.secondary">
                            {row.creator +
                              " created at " +
                              covertUtcToLocal(row.created_at) +
                              ". " +
                              row.last_updated_by +
                              " updated at " +
                              covertUtcToLocal(row.updated_at)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </CardActionArea>
                  <Stack
                    direction="row"
                    justifyContent="flex-end"
                    sx={{ px: 1, pb: 1 }}
                  >
                    <Tooltip title="Delete">
                      <IconButton
                        onClick={() => onDelete(row.id)}
                        aria-label="delete data source"
                        disabled={isDeleteDisabled(row)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>

      <ConfirmationDialog
        isOpen={isDialogOpen}
        onConfirm={handleConfirm}
        onClose={handleClose}
        message="Are you sure you want to delete this data source?"
      />

      <ErrorModal error={error} onClose={() => setError(null)} />
      {/* Add */}
      <AddDataSourceDialog
        open={openAdd}
        mode="add"
        currentUserId={currentUserId}
        currentTeamId={globalTeamId}
        userConfig={userConfig}
        effectiveRole={effectiveRole}
        onClose={() => {
          setOpenAdd(false);
          onRefresh();
        }}
      />

      {/* Edit */}
      <AddDataSourceDialog
        open={!!editRow}
        mode="edit"
        currentUserId={currentUserId}
        currentTeamId={globalTeamId}
        initial={editRow ?? undefined}
        userConfig={userConfig}
        effectiveRole={effectiveRole}
        onClose={() => {
          setEditRow(null);
          onRefresh();
        }}
      />
    </Box>
  );
}
